/**
 * `prop-types` isn't a project dependency (see the same note in
 * `RoutePlaceholder.jsx` / `AppProviders.jsx`), so `react/prop-types` is
 * disabled for this whole file rather than pulling in a new dependency —
 * `DrawerContent`'s `permissions` prop is accessed via several nested
 * property reads (`permissions.allowedToGenerateJwt`, etc.) that the rule
 * flags individually, which a single `eslint-disable-next-line` on the
 * function signature does not cover.
 */
/* eslint-disable react/prop-types */
import { useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import Drawer from '@mui/material/Drawer'
import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Typography from '@mui/material/Typography'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import ToggleButton from '@mui/material/ToggleButton'
import TextField from '@mui/material/TextField'
import useMediaQuery from '@mui/material/useMediaQuery'
import MenuIcon from '@mui/icons-material/Menu'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PeopleIcon from '@mui/icons-material/People'
import GavelIcon from '@mui/icons-material/Gavel'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import LogoutIcon from '@mui/icons-material/Logout'
import BlockIcon from '@mui/icons-material/Block'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import CampaignIcon from '@mui/icons-material/Campaign'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import ArticleIcon from '@mui/icons-material/Article'
import HistoryIcon from '@mui/icons-material/History'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import horizontalLogo from '../../assets/images/horizontal.svg'
import iconeAstrea from '../../assets/images/iconeAstrea.svg'
import { useAuth } from '../context/AuthContext'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { useAstreaEnvironment } from '../hooks/useAstreaEnvironment'
import { useAstreaHomologOverride } from '../hooks/useAstreaHomologOverride'

/**
 * Port of `app/main.html` + `app/main.js` (`mainController`) — the
 * AngularJS "workspace" shell (top navbar + side-nav) — to a React/MUI
 * layout component rendered by the `/workspace` layout route in
 * `AppRoutes.jsx`, with `<Outlet />` in place of the legacy `ui-view`.
 *
 * UI kit mapping (per `specs/react-migration/plan.md`, "UI Kit" decision —
 * MUI replaces Materialize/Bootstrap 3, not a 1:1 visual port):
 * - Materialize `side-nav` (`#slide-out`) -> MUI `Drawer`. The legacy
 *   side-nav was a fixed, always-visible drawer on large screens
 *   (`.side-nav.fixed`) and a slide-out overlay triggered by
 *   `.btn-sidenav` on small screens (`hide-on-large-only`) — reproduced
 *   here with `variant="permanent"` on desktop (MUI breakpoint `md` up)
 *   and `variant="temporary"` (open/close controlled by local state) below
 *   it, matching Materialize's own default side-nav breakpoint (992px ~=
 *   MUI's `md`).
 * - The top `navbar-fixed`/`nav` (only really useful on mobile in the
 *   legacy markup — its only content was the hamburger button,
 *   `hide-on-large-only`) -> MUI `AppBar`, hidden above the `md` breakpoint
 *   for the same reason (the permanent Drawer already occupies that space
 *   on desktop, exactly like `.side-nav.fixed` did).
 * - Materialize `collapsible collapsible-accordion` (the "Ações Comerciais"
 *   submenu wrapping "Cupom de desconto" / "Banner Login") -> MUI
 *   `Accordion`/`AccordionSummary`/`AccordionDetails`.
 *
 * Permission flags (`allowedToGenerateJwt`, `allowedToManageBanner`) are
 * ported from `mainController`'s `AuthService.getUser().then(...)` callback
 * via `useCurrentUser()` (`client/src/shared/hooks/useCurrentUser.js`),
 * which itself only *reads* `useAuth()` (task 009) — this task does not
 * modify `AuthContext.jsx`.
 *
 * Logout: `mainController.logout()` called `AuthService.logout()` then
 * `$state.go('login')`. Here that's `useAuth().logout()` followed by a
 * React Router `navigate('/login', { replace: true })`.
 */

const DRAWER_WIDTH = 280

/**
 * Menu item definitions, ported 1:1 from the `<li>` entries inside
 * `#slide-out` in `app/main.html` (in the same order), minus "Login
 * Administrativo" and "Ações Comerciais" which need special handling
 * (permission-gated / nested submenu respectively) and are rendered
 * separately below. Paths reference the React Router paths already
 * registered under `/workspace` in `AppRoutes.jsx` (task 002).
 */
const MAIN_MENU_ITEMS = [
  { label: 'Clientes', path: 'customers', icon: PeopleIcon },
  {
    label: 'Auditoria Menemosine',
    path: 'menemosine',
    icon: GavelIcon,
  },
  { label: 'Logout Users', path: 'logout-users', icon: LogoutIcon, permission: 'allowedToLogoutUsers' },
  { label: 'Inativador', path: 'inactivator', icon: BlockIcon },
  { label: 'Feature Toggle', path: 'feature-toggle', icon: SwapHorizIcon },
  { label: 'Migrações', path: 'migration', icon: SwapHorizIcon },
]

/** "Ações Comerciais" submenu (Materialize `collapsible-accordion` -> MUI `Accordion`). */
const COMMERCIAL_ACTIONS_MENU_ITEMS = [
  { label: 'Cupom de desconto', path: 'discount-coupons', icon: LocalOfferIcon },
  {
    label: 'Banner Login',
    path: 'banner',
    icon: CampaignIcon,
    permission: 'allowedToManageBanner',
  },
]

const ADMINISTRATION_MENU_ITEMS = [
  { label: 'Usuários', path: 'administration/users', icon: ManageAccountsIcon },
]

const TAIL_MENU_ITEMS = [
  { label: 'Auditoria de ações', path: 'action-audit', icon: ArticleIcon },
  { label: 'Consulta de publicações', path: 'clippings-list', icon: ArticleIcon },
  { label: 'Consulta de processos', path: 'histories', icon: HistoryIcon },
  { label: 'Robôs', path: 'robots', icon: SmartToyIcon, permission: 'allowedToUseRobots' },
]

function DrawerContent({ permissions, onNavigate }) {
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { urlAstrea, urlAstreaPath } = useAstreaEnvironment()
  const {
    target: homologOverrideTarget,
    setTarget: setHomologOverrideTarget,
    playgroundUrl,
    setPlaygroundUrl,
  } = useAstreaHomologOverride()
  const [playgroundUrlDraft, setPlaygroundUrlDraft] = useState(playgroundUrl)

  function handleNavigate(path) {
    navigate(path)
    onNavigate?.()
  }

  function handleLogout() {
    logout()
    navigate('/login', { replace: true })
  }

  function isVisible(item) {
    return !item.permission || permissions[item.permission]
  }

  return (
    <Box role="presentation" sx={{ width: DRAWER_WIDTH, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <img src={horizontalLogo} alt="Astrea" style={{ maxWidth: '100%', height: 'auto' }} />
      </Box>
      <Divider />
      <List component="nav" sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {MAIN_MENU_ITEMS.filter(isVisible).map(({ label, path, icon: Icon }) => (
          <ListItemButton key={path} onClick={() => handleNavigate(path)}>
            <ListItemIcon>
              <Icon />
            </ListItemIcon>
            <ListItemText primary={label} />
          </ListItemButton>
        ))}

        {permissions.allowedToGenerateJwt && (
          <ListItemButton onClick={() => handleNavigate('jwt')}>
            <ListItemIcon>
              <VpnKeyIcon />
            </ListItemIcon>
            <ListItemText primary="Login Administrativo" />
          </ListItemButton>
        )}

        <Accordion disableGutters elevation={0} square sx={{ backgroundColor: 'transparent' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="commercial-actions-content">
            <Typography>Ações Comerciais</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <List disablePadding>
              {COMMERCIAL_ACTIONS_MENU_ITEMS.filter(isVisible).map(({ label, path, icon: Icon }) => (
                <ListItemButton key={path} sx={{ pl: 4 }} onClick={() => handleNavigate(path)}>
                  <ListItemIcon>
                    <Icon />
                  </ListItemIcon>
                  <ListItemText primary={label} />
                </ListItemButton>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>

        {permissions.isAdministrator && (
          <Accordion disableGutters elevation={0} square sx={{ backgroundColor: 'transparent' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="administration-content">
              <Typography>Administração</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <List disablePadding>
                {ADMINISTRATION_MENU_ITEMS.map(({ label, path, icon: Icon }) => (
                  <ListItemButton key={path} sx={{ pl: 4 }} onClick={() => handleNavigate(path)}>
                    <ListItemIcon>
                      <Icon />
                    </ListItemIcon>
                    <ListItemText primary={label} />
                  </ListItemButton>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        )}

        {TAIL_MENU_ITEMS.filter(isVisible).map(({ label, path, icon: Icon }) => (
          <ListItemButton key={path} onClick={() => handleNavigate(path)}>
            <ListItemIcon>
              <Icon />
            </ListItemIcon>
            <ListItemText primary={label} />
          </ListItemButton>
        ))}
      </List>

      {urlAstrea === 'HOMOLOG' && (
        <>
          <Divider />
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary" component="div">
              Ambiente Astrea
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={homologOverrideTarget}
              onChange={(event, value) => value && setHomologOverrideTarget(value)}
              aria-label="Ambiente Astrea"
              sx={{ mt: 0.5 }}
            >
              <ToggleButton value="NONE" aria-label="Homologação">
                Homolog
              </ToggleButton>
              <ToggleButton value="LOCAL" aria-label="Astrea local">
                Local
              </ToggleButton>
              <ToggleButton value="PLAYGROUND" aria-label="Playground">
                Playground
              </ToggleButton>
            </ToggleButtonGroup>

            {homologOverrideTarget === 'PLAYGROUND' && (
              <TextField
                size="small"
                variant="standard"
                fullWidth
                placeholder="URL customizada (*.appspot.com) — opcional"
                value={playgroundUrlDraft}
                onChange={(event) => setPlaygroundUrlDraft(event.target.value)}
                onBlur={() => setPlaygroundUrl(playgroundUrlDraft.trim())}
                sx={{ mt: 1 }}
              />
            )}

            <Typography variant="caption" color="text.secondary" component="div" sx={{ wordBreak: 'break-all', mt: 0.5 }}>
              {urlAstreaPath}
            </Typography>
          </Box>
        </>
      )}

      <Divider />
      <List component="nav">
        <ListItemButton onClick={handleLogout}>
          <ListItemIcon>
            <ExitToAppIcon />
          </ListItemIcon>
          <ListItemText primary="Sair" />
        </ListItemButton>
      </List>
    </Box>
  )
}

function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false)
  // MUI's default theme breakpoint `md` (900px) is the closest equivalent to
  // Materialize's side-nav breakpoint (992px) used by `hide-on-large-only` /
  // `show-on-large` in `app/main.html`.
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('md'))
  const {
    allowedToGenerateJwt,
    allowedToResetElastic,
    allowedToLogoutUsers,
    allowedToManageBanner,
    allowedToManageSubscriptions,
    allowedToUseRobots,
    isAdministrator,
  } = useCurrentUser()

  const permissions = {
    allowedToGenerateJwt,
    allowedToResetElastic,
    allowedToLogoutUsers,
    allowedToManageBanner,
    allowedToManageSubscriptions,
    allowedToUseRobots,
    isAdministrator,
  }

  function handleDrawerToggle() {
    setMobileOpen((open) => !open)
  }

  function handleDrawerClose() {
    setMobileOpen(false)
  }

  return (
    <Box sx={{ display: 'flex' }}>
      {/*
        Equivalent of the legacy `.navbar-fixed` (`app/main.html`): in
        production the purple bar sits *beside* the fixed side-nav on
        desktop, not on top of it — Materialize achieves that via z-index
        layering (`.side-nav.fixed` z-index 998 > `.navbar-fixed nav` 997,
        so the opaque sidenav paints over the strip underneath it). MUI's
        permanent `Drawer` doesn't participate in that stacking trick the
        same way, so it's reproduced here explicitly instead: the bar's
        width/margin are inset by `DRAWER_WIDTH` from the `md` breakpoint
        up, the same point the permanent Drawer takes over from the
        temporary one below.
      */}
      <AppBar
        position="fixed"
        color="primary"
        elevation={1}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="abrir menu de navegação"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box component="img" src={iconeAstrea} alt="Astrea" sx={{ height: 32, mx: 'auto', display: { md: 'none' } }} />
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        {/* Mobile: temporary overlay drawer, closes after navigation/backdrop click (`hideSideNav()` equivalent). */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerClose}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
        >
          <DrawerContent permissions={permissions} onNavigate={handleDrawerClose} />
        </Drawer>

        {/*
          Desktop: permanent, always-visible drawer (`.side-nav.fixed`
          equivalent). The leading `<Toolbar />` is a spacer (not a second
          app bar) so the drawer's own content starts below the now
          always-visible `AppBar` above instead of being covered by it.
        */}
        <Drawer
          variant="permanent"
          sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' } }}
          open={isDesktop}
        >
          <Toolbar />
          <DrawerContent permissions={permissions} />
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          p: 2,
          mt: 7,
        }}
      >
        <Outlet />
      </Box>
    </Box>
  )
}

export default AppShell
